import { clsx, type ClassValue } from 'clsx'
import { addMinutes, format, formatISO, isValid, parseISO } from 'date-fns'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

function isValidDate(dateSt: string | null | undefined) {
  const date = new Date(dateSt ?? '')
  return !isNaN(date.getTime())
}

export function convertDateInputValueToBackendDate(
  date: string | undefined | null
) {
  if (date == null || !isValidDate(date)) {
    return null
  }

  return formatISO(new Date(date))
}

export function convertServerDateToInputDate(date: string | null | undefined) {
  if (date == null || !isValidDate(date)) {
    return undefined
  }

  return format(new Date(date), "yyyy-MM-dd'T'HH:mm")
}

export function formatDateTime(dateStr?: string | null): string {
  if (!dateStr) return ''

  try {
    const parsed = parseISO(dateStr)
    if (!isValid(parsed)) return ''

    return format(parsed, 'dd/MM/yyyy, hh:mm a')
  } catch (e) {
    console.log(e)
    return '' // fallback  parse
  }
}
export function formatForInput(dateStr?: string | null): string {
  if (!dateStr) return ''
  return format(parseISO(dateStr), "yyyy-MM-dd'T'HH:mm") // input[type=datetime-local]
}

export /**
 * Handle ISO time from backend Carbon
 * @param data date string
 * @returns
 */
function formatDisplayDateTable(data: string) {
  if (!data) return data

  const date = new Date(data)

  return format(addMinutes(date, date.getTimezoneOffset()), 'dd/MM/yyyy HH:mm')
}

export function formatJsonWithColor(json: object): string {
  const jsonString = JSON.stringify(json, null, 2)

  // Highlight strings, keys, numbers, booleans, null
  let formatted = jsonString.replace(
    /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|\b\d+(\.\d+)?\b)/g,
    (match) => {
      let cls = 'text-gray-300' // default

      if (/^"/.test(match)) {
        if (/:$/.test(match)) {
          cls = 'text-yellow-400 font-semibold' // key
        } else {
          cls = 'text-green-400' // string value
        }
      } else if (/true|false/.test(match)) {
        cls = 'text-purple-400 font-semibold' // boolean
      } else if (/null/.test(match)) {
        cls = 'text-pink-400 italic' // null
      } else if (/^[\d.]+$/.test(match)) {
        cls = 'text-yellow-300' // number
      }

      return `<span class="${cls}">${match}</span>`
    }
  )

  // Highlight brackets {} []
  formatted = formatted.replace(
    // eslint-disable-next-line no-useless-escape
    /([\{\}\[\]])/g,
    '<span class="text-white">$1</span>'
  )

  // Highlight , , to color white
  formatted = formatted.replace(/,/g, '<span class="text-white">,</span>')

  // Highlight : , to color white
  formatted = formatted.replace(/:/g, '<span class="text-white">:</span>')

  // change character \n to to <br>
  formatted = formatted.replace(/\\n/g, '<br />')

  formatted = formatted.replace(/\\/g, '')

  return formatted
}

export function formatAssistantText(text: string): string {
  if (!text) return ''

  const sentences = text
    .split(/(?<=\.)\s+/) // Separated after a period and followed by a space
    .map((sentence) => sentence.trim())
    .filter(Boolean)

  return sentences.map((s) => `<p class="mb-2">${s}</p>`).join('')
}
